import { c as _c } from "react/compiler-runtime";
import { LinkAwareContainer } from '@/components/Atoms/LinkAwareContainer/LinkAwareContainer';
import { trackMouseDown } from '@/components/Molecules/TrackableExternalLink/TrackableExternalLink';
import { useBVideoPlayer } from '@/hooks/useBVideoPlayer';
import { useToaster } from '@/hooks/useToaster';
import useTranslation from '@/hooks/useTranslation';
import { useTweetLoader } from '@/hooks/useTweetLoader';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { anchorJumpOffset } from '@/styles/patterns';
import { DateTimeFormat } from '@/utils/dateUtils';
import { sanitiseHtml } from '@/utils/strings';
import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
const RSFViewerComponentWrapper = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <RSFViewerComponent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const RSFViewer = memo(RSFViewerComponentWrapper);

/**
 * Utility function to add stats tracking to all links
 */
const addLinkTracking = (elm: HTMLAnchorElement) => {
  elm.addEventListener('mousedown', event => {
    trackMouseDown(event, () => {
      const href = elm.href;
      trackEvent(TrackingAction.FollowLink, TrackingCategory.ExternalContent, href);
    });
  });
};

/**
 * A a wrapper to format styled RSF elements.
 * The structure guidelines are defined on
 * https://cms.prod.bloomberg.com/team/display/nppub/RSF+User+Guide
 */

const RSFViewerComponent = t0 => {
  const $ = _c(8);
  const {
    htmlContent
  } = t0;
  const {
    lang
  } = useTranslation();
  const ref = useRef(null);
  let t1;
  if ($[0] !== htmlContent) {
    t1 = sanitiseHtml(htmlContent);
    $[0] = htmlContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sanitizedHtml = t1;
  useTweetLoader(ref, ".news-social-embedded[data-social-media-type=\"twitter\"]", "data-vendor-id", "news-social-twitter-widget", "data-original-url");
  useToaster(ref);
  useBVideoPlayer("bplayer-placeholder", _temp);
  let t2;
  if ($[2] !== lang) {
    t2 = () => {
      const links = Array.prototype.slice.call(document.querySelectorAll(".news-rsf-news-body a"));
      links.forEach(_temp2);
      const timeStamps = Array.prototype.slice.call(document.querySelectorAll(".news-timestamp"));
      timeStamps.forEach(stamp => {
        const targetUtcTime = stamp.getAttribute("data-utc");
        if (targetUtcTime) {
          stamp.textContent = DateTimeFormat(lang, {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric"
          }).format(new Date(targetUtcTime));
        }
      });
    };
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== sanitizedHtml) {
    t3 = [sanitizedHtml];
    $[4] = sanitizedHtml;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== sanitizedHtml) {
    t4 = <MemoedRsfNode sanitizedHtml={sanitizedHtml} ref={ref} />;
    $[6] = sanitizedHtml;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const RSFNode = forwardRef<HTMLDivElement, {
  sanitizedHtml: string;
}>((props, ref) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.sanitizedHtml) {
    t0 = {
      __html: props.sanitizedHtml
    };
    $[0] = props.sanitizedHtml;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== ref || $[3] !== t0) {
    t1 = <RSFStyledWrapper className="cds-prose" dangerouslySetInnerHTML={t0} ref={ref} />;
    $[2] = ref;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
});

// Some hooks have State changes such as useScript. This can cause React to re-render dangerouslySetInnerHTML, which may
// destroy all children / re-add them. The hooks would be hydrating detached children, no longer on screen.
// Using memo to prevent dangerouslySetInnerHTML from re-rendering despite state changes.
const MemoedRsfNode = memo(RSFNode);

/*
 * RSF TARGET SETTINGS
 * Definitions for the RSF classes we want to specifically style/target.
 */
const RevisionStandardFormatSettings = css`
    // Abstract body of the news, usually a list of bullets.

    .news-rsf-abstract:not(:empty) {
        padding: 0 0 var(--size-grid-unit) 0;
        font-size: var(--cds-font-body-xlarge-fontsize);
        line-height: var(--cds-font-body-xlarge-lineheight);
        letter-spacing: var(--cds-font-body-xlarge-letterspacing);
    }

    //A summarized version of the content

    .news-rsf-summary {
        font-size: var(--cds-font-body-xlarge-fontsize);
        line-height: var(--cds-font-body-xlarge-lineheight);
        letter-spacing: var(--cds-font-body-xlarge-letterspacing);
    }

    // Bold bases.

    .news-rsf-tagline {
        font-weight: bold;
    }

    .news-first-publish-date {
        font-style: italic;
    }

    // Trashline providing more details.

    .news-trashline {
        font-style: italic;
        margin-bottom: var(--size-grid-half);
    }

    .news-social-embedded {
        margin-bottom: var(--size-grid-unit);
        padding: var(--size-grid-unit);
        width: 100%;
        border: 1px solid var(--color-lines-prose);
        border-radius: var(--size-grid-quarter);

        .news-social-person {
            img {
                float: left;
                margin-right: var(--size-grid-half);
            }

            .news-social-verified-icon {
                display: none;
            }

            a {
                // The "@username" line
                display: block;
            }
        }

        > *:not(:last-child) {
            margin-bottom: var(--size-grid-half);
        }
    }

    .news-social-twitter-widget {
        margin-bottom: var(--size-grid-unit);
        width: 100%;

        & > :first-child {
            margin: 0 auto;
        }
    }

    .news-footnotes {
        // Separate from the main content with some spacing.
        margin: var(--size-grid-double) 0 0 0;

        p {
            font-size: var(--cds-font-body-default-fontsize);
            line-height: var(--cds-font-body-default-lineheight);
            letter-spacing: var(--cds-font-body-default-letterspacing);
            margin: 0;
            padding: 0;
        }

        li {
            list-style-type: decimal;
            font-size: var(--cds-font-body-default-fontsize);
            line-height: var(--cds-font-body-default-lineheight);
            letter-spacing: var(--cds-font-body-default-letterspacing);
            margin: 0 0 var(--size-grid-quarter) var(--size-grid-unit);
            padding: 0;
            ${anchorJumpOffset};
        }

        blockquote {
            padding: 0;
        }
    }

    .rsf-table-wrapper {
        overflow: auto;
        margin-bottom: var(--size-grid-unit);
        border-bottom: 1px solid var(--color-lines-tableBorder);
        border-top: 1px solid var(--color-lines-tableBorder);
    }
`;
const LiveBlogTagSettings = css`
    .news-rsf-full-content {
        margin-bottom: var(--size-grid-unit);
    }

    .tliv-rsf-post {
        padding: var(--size-grid-unit);
        border-top: 1px solid var(--color-lines-prose);
        border-bottom: 1px solid var(--color-lines-prose);
        margin-bottom: var(--size-grid-unit);
    }

    .tliv-rsf-contrib {
        font-size: var(--cds-font-body-default-fontsize);
        line-height: var(--cds-font-body-default-lineheight);
        letter-spacing: var(--cds-font-body-default-letterspacing);
        font-weight: bold;

        .news-author-caption {
            font-weight: normal;
        }
    }

    .tliv-rsf-content {
        padding: var(--size-grid-half);
    }

    .tliv-rsf-title {
        margin-bottom: var(--size-grid-half);
        font-weight: bold;

        &:empty {
            display: none;
        }
    }

    .news-timestamp {
        font-size: var(--cds-font-body-default-fontsize);
        line-height: var(--cds-font-body-default-lineheight);
        letter-spacing: var(--cds-font-body-default-letterspacing);
    }

    .news-tout-title {
        font-weight: bold;
        display: block;
    }
`;

/*
 * GENERAL SEMANTIC STYLING
 * Style for semantic elements found in the documents.
 * One can define more specific styling by targeting the wrapper classes.
 */
const GeneralContentTagSettings = css`
    // Quotations.

    blockquote {
        font-style: italic;
        padding: var(--size-grid-half);

        h3 {
            font-size: var(--cds-font-body-large-fontsize);
            line-height: var(--cds-font-body-large-lineheight);
            letter-spacing: var(--cds-font-body-large-letterspacing);
            margin-bottom: var(--size-grid-unit);
        }

        p:not(:last-child) {
            margin-bottom: var(--size-grid-half);
        }
    }

    // Superscripts (usually footnote references).

    sup {
        padding: var(--size-grid-quarter);
    }

    // Exhibits and figures.

    figure {
        padding: 0;
        margin: 0 0 var(--size-grid-double) 0;

        figcaption {
            .news-figure-caption-text {
                font-style: italic;

                p {
                    padding: 0;
                    margin: 0;
                }
            }

            .news-figure-credit {
                p {
                    padding: 0;
                    margin: 0;
                }
                font-size: var(--cds-font-body-default-fontsize);
                line-height: var(--cds-font-body-default-lineheight);
                letter-spacing: var(--cds-font-body-default-letterspacing);
                font-weight: bold;
            }
        }

        &[data-align='right'] {
            float: right;
            width: 50%;
            margin-left: var(--size-grid-unit);
        }

        &[data-align='left'] {
            float: left;
            width: 50%;
            margin-right: var(--size-grid-unit);
        }
    }
`;
const RSFStyledWrapper = styled(LinkAwareContainer)`
    ${RevisionStandardFormatSettings};
    ${LiveBlogTagSettings};
    ${GeneralContentTagSettings};
`;
interface IProps {
  htmlContent: string;
}
function _temp(id, index) {
  return trackEvent(TrackingAction.Play, TrackingCategory.BloombergVideo, id, index);
}
function _temp2(link) {
  if (link.getAttribute("href")?.startsWith("/")) {
    addLinkTracking(link);
  } else {
    if (link.hostname === "blinks.bloomberg.com") {
      addLinkTracking(link);
    } else {
      if (link.protocol === "bbg:") {
        addLinkTracking(link);
      } else {
        addLinkTracking(link);
      }
    }
  }
}